import { useCallback, useEffect, useState } from 'preact/hooks'
import { sendingDomainsApi } from '@/api/sendingDomains'
import type { SendingDomainForecastRequestDto, SendingDomainForecastViewDto } from '@/types/sendingDomains'

// The forecast reads the whole audience, so a burst of edits (typing a send time, ticking several lists)
// asks once, after the form has settled.
export const FORECAST_SETTLE_MS = 500

interface Answer {
  key: string
  forecast: SendingDomainForecastViewDto | null
  error: string | null
}

export interface SendingDomainForecastResult {
  /** The last forecast that came back, whichever form it answered. Its Domains label the select. */
  latest: SendingDomainForecastViewDto | null
  /** The forecast for the form as it is now; null while a newer one is being asked for, or when it failed. */
  current: SendingDomainForecastViewDto | null
  /** Why the forecast for the form as it is now could not be read. */
  error: string | null
  /** Asks again about the same form, e.g. after Save was refused because the domain changed state. */
  refresh: () => void
}

/**
 * How the selected sending domain would carry the activation editor's unsaved form (ORB-953). Asks only
 * when the request changes, which is the domain, the send rule or the lists, and only once the form has
 * settled; an answer to a form that has since changed is dropped. A null request asks nothing.
 */
export function useSendingDomainForecast(
  workspaceId: string,
  webId: string,
  request: SendingDomainForecastRequestDto | null,
): SendingDomainForecastResult {
  const key = request ? JSON.stringify(request) : null
  const [answer, setAnswer] = useState<Answer | null>(null)
  const [latest, setLatest] = useState<SendingDomainForecastViewDto | null>(null)
  const [asks, setAsks] = useState(0)

  useEffect(() => {
    if (!key) return
    let superseded = false
    const timer = setTimeout(() => {
      const asked = JSON.parse(key) as SendingDomainForecastRequestDto
      void sendingDomainsApi.forecast(workspaceId, webId, asked).then(response => {
        if (superseded) return
        if (response.error || !response.data) {
          setAnswer({ key, forecast: null, error: response.error || 'No forecast came back.' })
          return
        }
        setAnswer({ key, forecast: response.data, error: null })
        setLatest(response.data)
      })
    }, FORECAST_SETTLE_MS)
    return () => {
      superseded = true
      clearTimeout(timer)
    }
  }, [workspaceId, webId, key, asks])

  const refresh = useCallback(() => setAsks(count => count + 1), [])
  const answersNow = !!answer && answer.key === key
  return {
    latest,
    current: answersNow ? answer.forecast : null,
    error: answersNow ? answer.error : null,
    refresh,
  }
}
